import {
  FC,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useState
} from "react";
import BottomSheet, {
  BottomSheetBackdrop,
  BottomSheetScrollView
} from "@gorhom/bottom-sheet";
import { BottomSheetContext } from "@/_hooks/useBottomSheet";

interface IBottomSheetProviderProps {
  children: ReactNode;
  onRenderContent: (activeSheet: number | null) => ReactNode;
}

const BottomSheetProvider: FC<IBottomSheetProviderProps> = ({
  children,
  onRenderContent
}) => {

  const bottomSheetRef = useRef<BottomSheet>(null);
  const [activeSheet, setActiveSheet] = useState<number | null>(null);

  const openSheet = useCallback((sheetIndex: number) => {
    setActiveSheet(sheetIndex);
    bottomSheetRef.current?.expand();
  }, []);

  const closeSheet = useCallback(() => {
    setActiveSheet(null);
    bottomSheetRef.current?.close();
  }, []);

  const handleSheetChange = useCallback((index: number) => {
    if (index === -1) setActiveSheet(null);
  }, []);

  const value = useMemo(() => ({
    openSheet,
    closeSheet,
    setActiveSheet
  }), [openSheet, closeSheet]);

  return (
    <BottomSheetContext.Provider value={value}>
      {children}
      <BottomSheet
        ref={bottomSheetRef}
        index={activeSheet !== null ? 0 : -1}
        snapPoints={["90%"]}
        enablePanDownToClose
        onChange={handleSheetChange}
        backdropComponent={(props) => (
          <BottomSheetBackdrop
            {...props}
            disappearsOnIndex={-1}
            appearsOnIndex={0}
            pressBehavior="close"
          />
        )}
      >
        <BottomSheetScrollView>
          {onRenderContent(activeSheet)}
        </BottomSheetScrollView>
      </BottomSheet>
    </BottomSheetContext.Provider>
  );
};

export default BottomSheetProvider;